import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const SettingsPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [settings, setSettings] = useState({
    email_notifications: true, job_alerts: true, message_notifications: true,
    payment_notifications: true, weekly_digest: true, profile_visible: true,
    show_email: false, show_phone: false, timezone: 'America/Los_Angeles',
    language: 'en', currency: 'USD'
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getSettings();
        setSettings(prev => ({ ...prev, ...data }));
      } catch {}
    };
    load();
  }, [user]);

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    try { await api.updateSettings(settings); } catch {}
    toast({ title: 'Settings saved!', description: 'Your preferences have been updated.' });
  };

  const tabs = ['Account', 'Notifications', 'Privacy', 'Preferences'];

  const Toggle = ({ checked, onChange }) => (
    <button onClick={onChange} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-[#0A3900]' : 'bg-gray-300'}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-4xl mx-auto px-8 py-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Settings</h1>

          <div className="flex gap-2 mb-6">
            {tabs.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab.toLowerCase())}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.toLowerCase() ? 'bg-[#0A3900] text-white' : 'bg-white text-gray-700 border hover:bg-gray-50'}`}>
                {tab}
              </button>
            ))}
          </div>

          {activeTab === 'account' && (
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Account Information</h2>
              <div className="space-y-4">
                <div><Label>Email</Label><Input className="mt-2" value={user?.email || ''} disabled /></div>
                <div><Label>Full Name</Label><Input className="mt-2" defaultValue={user?.name || ''} /></div>
                <div><Label>Phone Number</Label><Input className="mt-2" placeholder="+1 (555) 000-0000" /></div>
                <Button onClick={handleSave} className="bg-[#0A3900] hover:bg-[#1A5C0A]">Save Changes</Button>
              </div>
              <div className="mt-8 pt-6 border-t">
                <h3 className="font-semibold text-red-600 mb-2">Danger Zone</h3>
                <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50">Deactivate Account</Button>
                <p className="text-sm text-gray-500 mt-2">Temporarily disable your account. You can reactivate anytime.</p>
              </div>
            </Card>
          )}

          {activeTab === 'notifications' && (
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Notification Preferences</h2>
              <div className="space-y-6">
                {[
                  ['email_notifications', 'Email Notifications', 'Receive notifications via email'],
                  ['job_alerts', 'Job Alerts', 'Get notified about new matching jobs'],
                  ['message_notifications', 'Message Notifications', 'Notifications for new messages'],
                  ['payment_notifications', 'Payment Notifications', 'Alerts for payments and earnings'],
                  ['weekly_digest', 'Weekly Digest', 'Summary of weekly activity']
                ].map(([key, label, desc]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div><p className="font-medium text-gray-900">{label}</p><p className="text-sm text-gray-500">{desc}</p></div>
                    <Toggle checked={settings[key]} onChange={() => handleToggle(key)} />
                  </div>
                ))}
              </div>
              <Button onClick={handleSave} className="mt-6 bg-[#0A3900] hover:bg-[#1A5C0A]">Save Preferences</Button>
            </Card>
          )}

          {activeTab === 'privacy' && (
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Privacy Settings</h2>
              <div className="space-y-6">
                {[
                  ['profile_visible', 'Profile Visibility', 'Make your profile visible to companies'],
                  ['show_email', 'Show Email', 'Display email on your profile'],
                  ['show_phone', 'Show Phone', 'Display phone number on your profile']
                ].map(([key, label, desc]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div><p className="font-medium text-gray-900">{label}</p><p className="text-sm text-gray-500">{desc}</p></div>
                    <Toggle checked={settings[key]} onChange={() => handleToggle(key)} />
                  </div>
                ))}
              </div>
              <Button onClick={handleSave} className="mt-6 bg-[#0A3900] hover:bg-[#1A5C0A]">Save Privacy Settings</Button>
            </Card>
          )}

          {activeTab === 'preferences' && (
            <Card className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Preferences</h2>
              <div className="space-y-6">
                <div>
                  <Label>Timezone</Label>
                  <select className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md" value={settings.timezone} onChange={(e) => setSettings({...settings, timezone: e.target.value})}>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option><option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Chicago">Central Time (CT)</option><option value="America/New_York">Eastern Time (ET)</option>
                    <option value="Europe/London">GMT</option><option value="Asia/Kolkata">IST</option>
                  </select>
                </div>
                <div>
                  <Label>Language</Label>
                  <select className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md" value={settings.language} onChange={(e) => setSettings({...settings, language: e.target.value})}>
                    <option value="en">English</option><option value="es">Spanish</option><option value="fr">French</option><option value="de">German</option>
                  </select>
                </div>
                <div>
                  <Label>Currency</Label>
                  <select className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md" value={settings.currency} onChange={(e) => setSettings({...settings, currency: e.target.value})}>
                    <option value="USD">USD ($)</option><option value="EUR">EUR (€)</option><option value="GBP">GBP (£)</option><option value="INR">INR (₹)</option>
                  </select>
                </div>
              </div>
              <Button onClick={handleSave} className="mt-6 bg-[#0A3900] hover:bg-[#1A5C0A]">Save Preferences</Button>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
